import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { OnClick } from '@rbi-ctg/frontend';
import IconTimsBarcode from 'components/icons/tims-barcode';
import IconTimsQRCode from 'components/icons/tims-qrcode';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { useUIContext } from 'state/ui';

interface ICard {
  header: string;
  subHeader: string;
  icon: React.ReactNode;
  onClick: OnClick;
}

const CardContainer = styled.button`
  display: flex;
  flex-direction: row;
  background: ${Styles.color.white};
  padding: 20px;
  height: 71px;
  margin: 0 20px 20px 20px;
  border-radius: 10px;
  box-shadow: ${Styles.boxShadow};
  border: 0px solid transparent;
  cursor: pointer;
  &:active {
    transform: scale(0.95);
    transition: transform 200ms ease-out;
  }

  &[disabled] {
    cursor: not-allowed;
  }
`;

const CardInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  letter-spacing: -0.35px;
  font-weight: ${Styles.fontWeight.heavy};
  font-family: ${Styles.fontFamily.base};
  font-size: 1rem;

  color: ${Styles.color.black};
`;

const CardSubHeader = styled.div`
  display: flex;
  font-size: 0.9375rem;
  color: ${Styles.color.grey.one};
  font-family: ${Styles.fontFamily.base};
`;

const IconContainer = styled.div`
  display: flex;
  align-self: center;
`;

export const Card = ({ header, subHeader, icon, onClick }: ICard) => {
  return (
    <CardContainer onClick={onClick}>
      <CardInfo>
        <CardHeader>{header}</CardHeader>
        <CardSubHeader>{subHeader}</CardSubHeader>
      </CardInfo>
      <IconContainer>{icon}</IconContainer>
    </CardContainer>
  );
};

const Container = styled.section`
  display: flex;
  flex-direction: column;
  padding: 15px 0 20px 0;
`;

const ScanForRewards = () => {
  const mParticle = useMParticleContext();
  const { formatMessage } = useIntl();
  const { showCardTab, showRewardsTab } = useUIContext();

  const logButtonClickAndShowCardTab = useCallback(() => {
    mParticle.logNavigationClick(CustomEventNames.BUTTON_CLICK_SCAN_TO_PAY);
    showCardTab();
  }, [mParticle, showCardTab]);

  const logButtonClickAndShowRewardsTab = useCallback(() => {
    mParticle.logNavigationClick(CustomEventNames.BUTTON_CLICK_SCAN_FOR_REWARDS);
    showRewardsTab();
  }, [mParticle, showRewardsTab]);

  return (
    <Container>
      <Card
        header={formatMessage(messages.scanForTimsRewards)}
        subHeader={formatMessage(messages.ifYouArePayingInRestaurant)}
        onClick={logButtonClickAndShowRewardsTab}
        icon={<IconTimsBarcode fill={Styles.color.secondary} />}
      />
      <Card
        header={formatMessage(messages.scanToPayWithTimCard)}
        subHeader={formatMessage(messages.withYourDigitalTimCard)}
        onClick={logButtonClickAndShowCardTab}
        icon={<IconTimsQRCode />}
      />
    </Container>
  );
};

export default ScanForRewards;
const messages = defineMessages({
  withYourDigitalTimCard: {
    id: 'withYourDigitalTimCard',
    description: 'No description',
  },

  scanToPayWithTimCard: {
    id: 'scanToPayWithTimCard',
    defaultMessage: 'Scan to Pay with Tim Card',
    description: 'No description',
  },

  ifYouArePayingInRestaurant: {
    id: 'ifYouArePayingInRestaurant',
    defaultMessage: 'if you are paying in-restaurant',
    description: 'No description',
  },

  scanForTimsRewards: {
    id: 'scanForTimsRewards',
    defaultMessage: 'Scan for Tims™ Rewards',
    description: 'No description',
  },
});
